import * as React from "react";
import {Component } from "react";

import {observer} from "mobx-react";
import EsriGraphic from "@arcgis/core/Graphic";
import { Item, Table, Popup as SemanticPopup} from "semantic-ui-react";
import {NavigationConfiguration} from "../../../config/navigation-configuration";
import {ProjektTable, ProjektDataItem} from "./tables/projekt-table";
import {GebaeudeTable} from "./tables/gebaeude-table";
import {ZonenTable} from "./tables/zonen-table";
import {BodenTable} from "./tables/boden-table";
import {Icon} from "../../../core/components/icon/icon";
import {Grundstueck, Bodenbedeckung, Zone} from "../../../models/grundstueck";
import _ from "lodash";
import {autorun, runInAction} from "mobx";
import {ContentInfoBox} from "./content-info-box";
import {
    PopupPanelActionOea
} from "../../../core/components/popup/popup-panel/popup-panel-actions/popup-panel-action-oea";
import {withStore} from "../../../core/stores/abstract-app-store";
import {MapViewerStore} from "../../../stores/map-viewer-store-mobx";

export interface ObjectResultGrundstueckProps {
    store?: MapViewerStore; // this is injected by withStore
    grundstueck: Grundstueck | null;
    prepareKontakt: (grundstueck: Grundstueck) => void;
    handleErrorAndNotFound: () => void;
    handleHighlight: (grundstueck: Grundstueck, geometry: __esri.Geometry) => void;
    handleHighlightClick: (e: React.MouseEvent | undefined,
        item: Bodenbedeckung | Zone | null, layerNo?: number) => void;
    handleShowGebaeude: (egid: number, e: React.MouseEvent) => void;
    handleShowProjekt: (bage: string, e: React.MouseEvent) => void;
}

@observer
class ObjectResultGrundstueckBase extends Component<ObjectResultGrundstueckProps, any> {
    watchHandles: any[] = [];
    constructor(props: ObjectResultGrundstueckProps) {
        super(props);
        this.state = {
            highlightedItemId: null,
            graphic: null
        };
    }

    componentDidMount(): void {
        const { grundstueck, store,
            handleErrorAndNotFound, prepareKontakt, handleHighlight } = this.props;
        autorun((reaction: any) => {
            if (!store || !store.mapStore.view) {
                return;
            }
            if (!grundstueck || !grundstueck.nummer) {
                handleErrorAndNotFound();
                return;
            }
            reaction.dispose();
            prepareKontakt(grundstueck);
            const graphic = new EsriGraphic(grundstueck);
            handleHighlight(grundstueck, graphic.geometry);
            const newRoute = "/grundstueck/" + grundstueck.egrid;
            const breadcrumbs = new NavigationConfiguration();
            breadcrumbs.items = [
                { title: "Grundstück" },
                { href: newRoute, title: grundstueck.egrid }
            ];
            this.setState({graphic});
            runInAction(()=> {
                store.appStore.setPageTitle("Grundstück Nr. " + graphic.getAttribute("NUMMER")
                    + " in " + graphic.getAttribute("GEMEINDE")
                    + " (" + graphic.getAttribute("GBPER_NAME") + ")");
                store.appStore.setTopContent(null);
                store.appStore.setBottomContent(null);
                store.appStore.setBreadcrumbs(breadcrumbs);
                store.appStore.setRoutePath(newRoute);
                store.appStore.setObjectId(grundstueck.egrid);
                store.appStore.setTopicById("grundstueck");
            });
        });
    }

    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }
    handleHighlightClick(e: React.MouseEvent | undefined,
                         item: Bodenbedeckung | Zone | null,
                         layerNo?: number | undefined): void {
        this.props.handleHighlightClick(e, item, layerNo);
        this.setState({highlightedItemId: item?.itemId});
    }
    render(): React.ReactElement | null {
        const { grundstueck, store } = this.props;
        const {graphic, highlightedItemId} = this.state;
        if (!store || !store.mapStore.view) {
            return null;
        }
        if (!grundstueck || !grundstueck.nummer) {
            return null;
        }
        const { appConfig, mapViewerConfig, mapStore, oStore } = store;
        // const dataString = JSON.stringify(grundstueck.data, null,2);
        let infoBlock: React.ReactElement | null = null;
        const flaeche = new Intl.NumberFormat("de-CH").format(Math.round(grundstueck.flaeche));
        const boden = _.orderBy(grundstueck.bodenbedeckung,
            ["art", "gebaeudenummern", "flaeche"],
            ["asc", "asc", "desc"]);
        let flurnamen  = grundstueck.data.operationResults.AVGSBNAM_DS.features.filter(
            (item: any) => item.attributes.EGRIS_EGRID === grundstueck.egrid);
        flurnamen = _.sortBy(flurnamen, ["attributes.NAME"]);
        const zonen  = [...grundstueck.zonen.values()]; // grundstueck.data.operationResults.ZPGNDNTZ_DS.features;
        const oeaAction = appConfig.predefinedActions.find((action) => action.id === "oea");
        let oeaButton: React.ReactElement | null = null;
        if (oeaAction) {
            oeaButton = (
                <PopupPanelActionOea
                    actionConfig={oeaAction}
                    buttonAsLink={true}
                    graphic={graphic}>
                </PopupPanelActionOea>
            );
        }
        const description = (<Item.Content><Item.Header as={"h3"}>Grundstückbeschreibung</Item.Header>
            <Item.Description>
                <Table basic="very" compact>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell singleLine>Grundstücknummer</Table.Cell>
                            <Table.Cell>{grundstueck.nummer}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine>Eidg. Grundstück-ID (EGRID)</Table.Cell>
                            <Table.Cell>{grundstueck.egrid}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine>Gemeinde (BFS-Nr.)</Table.Cell>
                            <Table.Cell>
                                {grundstueck.attributes.GEMEINDE} ({grundstueck.attributes.BFS_NR})
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine>Grundbuch (GB-Nr.)</Table.Cell>
                            <Table.Cell>
                                {grundstueck.attributes.GBPER_NAME} ({grundstueck.attributes.GBPER_CODE})
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Grundstückart</Table.Cell>
                            <Table.Cell>{grundstueck.art}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell singleLine verticalAlign="top">Flurnamen</Table.Cell>
                            <Table.Cell><ul className={"flurnamen" + ((flurnamen.length > 1) ? " multi" : "") }>{
                                flurnamen.map((item: any) => (
                                    <li key={item.attributes.OBJECTID}>
                                        {item.attributes.NAME}
                                    </li>
                                ))}</ul></Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell verticalAlign="top"><SemanticPopup
                                wide
                                content={(<div>
                                    <p>Klicken Sie auf einen Eintrag, um die Fläche in der Karte hervorzuheben.</p>
                                </div>)}
                                trigger={<span>Bodenbedeckung <Icon name={"information"} /></span>} /></Table.Cell>
                            <Table.Cell><BodenTable data={boden}
                                highlightedItemId={highlightedItemId}
                                handleHighlightClick={(e: React.MouseEvent | undefined, item: Bodenbedeckung | null) =>
                                    this.handleHighlightClick(e, item)}
                                key={"boden-" + grundstueck.egrid}/>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Fläche (grundbuchlich)</Table.Cell>
                            <Table.Cell textAlign="right">{flaeche} m²</Table.Cell>
                        </Table.Row>
                        {(oeaAction) ? (
                            <Table.Row>
                                <Table.Cell verticalAlign="top"><SemanticPopup
                                    wide
                                    content={(<div>
                                        <p>Das Grundbuch enthält privatrechtliche Informationen und Beschränkungen.
                                            Die Eigentümerabfrage für einzelne Grundstücke ist öffentlich. Als Schutz
                                            vor Serienabfragen sind pro Tag und Mobile-Nummer nur 10 Abfragen möglich.
                                        </p>
                                    </div>)}
                                    trigger={<span>Eigentümer <Icon name={"information"} /></span>} /></Table.Cell>
                                <Table.Cell>
                                    {oeaButton}
                                </Table.Cell>
                            </Table.Row> ) : null }
                    </Table.Body>
                </Table></Item.Description>
        </Item.Content>);
        const projektData: ProjektDataItem[] = [];
        if (grundstueck.projekte) {
            grundstueck.projekte.forEach((projekt: any) => {
                projektData.push({
                    bagenr: projekt.bagenr,
                    gegenstand: projekt.gegenstand,
                    status: projekt.status
                });
            });
        }
        const infoItems: React.ReactElement[] = [];
        let gravisUrl = null;
        let oerebUrl = null;
        let zonenUrl = null;
        const gravisAction = appConfig.predefinedActions.find((action) => action.id === "gravis");
        if (gravisAction && gravisAction.url) {
            if (mapStore.view) {
                gravisUrl = gravisAction.url + "PARCEL=" + grundstueck.attributes.GBPER_CODE + "." + grundstueck.nummer
                    + "&Suche.Gemeinde=" + grundstueck.attributes.BFS_NR;
                if(mapStore.view?.center) {
                    const center = mapStore.view.center;
                    gravisUrl+= "&FOCUS=" + center.x + ":" + center.y + ":" + mapStore.view.scale;
                }
            }
        }
        const oerebAction = appConfig.predefinedActions.find((action) => action.id === "oereb");
        if (oerebAction && oerebAction.url) {
            oerebUrl = oerebAction.url.replace("{EGRID}", grundstueck.egrid);
        }
        const zonenAction = appConfig.predefinedActions.find((action) => action.id === "nutzung");
        let zonenButton: React.ReactElement | undefined;
        if (zonenAction && zonenAction.url) {
            zonenUrl = zonenAction.url + "PARCEL=" + grundstueck.attributes.GBPER_CODE + "." +  grundstueck.nummer;
            zonenButton = (<a className={"link"} href={zonenUrl} target={"_blank"} rel={"noreferrer"}>
                <span className={"linked"}>Grundstück in der Karte {zonenAction.title} anzeigen</span>
            </a>);
        }
        infoItems.push(
            <ContentInfoBox
                mapViewerConfig={mapViewerConfig}
                key={"gravis"}
                type={"gravis"}
                url={gravisUrl}
            />
        );
        infoItems.push(
            <ContentInfoBox
                mapViewerConfig={mapViewerConfig}
                key={"oereb"}
                type={"oereb"}
                url={oerebUrl}
            />
        );
        if (infoItems.length > 0) {
            infoBlock = (<Item.Group className={"geo-more-info"} divided>{infoItems}</Item.Group>);
        }

        return (<div>
            <Item.Group>
                <Item>{description}</Item>
                <ZonenTable data={zonen}
                    highlightedItemId={highlightedItemId}
                    handleHighlightClick={(e: React.MouseEvent | undefined, zone: Zone | null) =>
                        this.handleHighlightClick(e, zone)}
                    link={zonenButton}
                    key={"grund-" + grundstueck.egrid} />
            </Item.Group>
            <Item.Group className={"geo-gebaeude-items"}>
                <GebaeudeTable
                    data={grundstueck.gebaeude}
                    handleShowGebaeude={(egid: number, event: React.MouseEvent)=>
                        this.props.handleShowGebaeude(egid, event)}
                    key={"zonen-" + grundstueck.egrid} />
            </Item.Group>
            <Item.Group className={"geo-projekte-items"}>
                <ProjektTable
                    data={projektData}
                    key={oStore.selectedObjektId}
                    handleShowProjekt={(bagenr: string, event: React.MouseEvent)=>
                        this.props.handleShowProjekt(bagenr, event)}/>
            </Item.Group>
            {infoBlock}
        </div>);
    }
}

export const ObjectResultGrundstueck = withStore(ObjectResultGrundstueckBase);
